import { useContext } from 'react';

import { useSubscription } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { ComponentController } from '@r-core/controller';

/**
 * Keeps a blueprint sheet in step with dimension changes made anywhere in the app: the working
 * view's "show hidden dimensions" eyeball, a single dimension being hidden or shown, AND a single
 * dimension being rotated via Rotate 90° (`dimension_custom_angle`, GitHub #603 — rotation wrote
 * the model but the sheet never repainted because the exact `^dimension$` match did not reach it).
 * A sheet builds its own controller and mirrors the main options once, inside a useMemo that does not
 * depend on them, and its React.memo comparator only looks at identity props -- so the container's own
 * ['dimension','updated'] subscription never reaches the sheet, and the eyeball moved nothing but its
 * own icon. Subscribing here re-renders the sheet itself, which memo cannot block. The assignment must
 * stay in render: nothing subscribes to the sheet's controller to pick up a change made in an effect.
 */
export const useSheetDimensionVisibility = (pageController: ComponentController): void => {
	const mainController = useContext(AppContext)?.controller ?? null;

	useSubscription(mainController, false, [['designer-options-show-all-dimensions', 'updated']]);
	useSubscription(pageController?.kitchen ?? null, false, [['(dimension|dimension_custom_angle)', 'updated']]);

	const showAllDimensions = mainController?.options?.showAllDimensions ?? false;
	if (pageController.options.showAllDimensions !== showAllDimensions) {
		pageController.options.showAllDimensions = showAllDimensions;
	}
};
